import type { Position } from '../types';

/**
 * ¿Es esta carga del flujo self-managed?
 *
 * Fuente única de verdad, compartida por /positions y /stats: si cada pantalla
 * usara su propio criterio, el mismo día podría contar distinto en cada una.
 *
 * Lo decide la planning y lo manda el backend (`isSelfManaged`). El respaldo
 * por proveedor/producto existe solo para que las pantallas sigan funcionando
 * contra una API todavía sin desplegar; en cuanto llega el flag deja de usarse.
 * Ese respaldo no detecta las plannings que crea un admin desde el portal, que
 * nacen con los placeholder y sin proveedor.
 */
export function isSelfManagedCargo(p: Position): boolean {
  if (typeof p.isSelfManaged === 'boolean') return p.isSelfManaged;
  return Boolean(p.selfManagedProductName || p.supplierName);
}

/**
 * Producto de relleno de la maestra clásica. Existe solo para que la FK
 * NOT NULL de Cargo/planning tenga a qué apuntar cuando el producto real vive
 * en el catálogo self-managed. Nunca debe llegar a verse en pantalla.
 *
 * Se compara normalizado (sin espacios ni guiones, en mayúsculas) para que un
 * "Self-Managed (Placeholder)" tecleado distinto se detecte igual.
 */
const PLACEHOLDER_PRODUCT = 'SELFMANAGEDPLACEHOLDER';

function isPlaceholderProduct(name: string): boolean {
  return name.toUpperCase().replace(/[^A-Z]/g, '') === PLACEHOLDER_PRODUCT;
}

/**
 * TODOS los productos de la carga, tal como los enseña la columna Product del
 * portal, o undefined si no hay ninguno real.
 *
 * En self-managed manda el catálogo nuevo, pero se admite el producto de la
 * maestra clásica como respaldo: una planning creada desde el PORTAL nunca
 * lleva producto del catálogo nuevo —esa pantalla no sabe asignarlo— y sin este
 * respaldo aparecía en la app sin producto, aunque el administrador se lo
 * hubiera puesto. Lo único que nunca se muestra es el placeholder.
 *
 * El servidor parte la lista en dos campos: con dos productos manda el
 * primero en `productName` y el segundo en `otherProductName`; con tres o
 * más los junta ya en `productName`. Hasta el 2026-09-11 la card, el detalle
 * y la edición pintaban solo `productName`, así que una planning con dos
 * productos enseñaba uno. Vale para los dos flujos: en self-managed manda el
 * catálogo propio si lo hay, y si no, los de la maestra sin el relleno.
 */
export function displayProductNames(p: Position): string | undefined {
  const own = (p.selfManagedProductName ?? '').trim();
  if (own) return own;
  const names = [p.productName, p.otherProductName]
    .map((name) => (name ?? '').toString().trim())
    .filter((name) => name && !isPlaceholderProduct(name));
  return names.length > 0 ? names.join(', ') : undefined;
}

/**
 * La nota de producto que se tecleó en la planning del portal, SOLO si la
 * carga no tiene ningún producto de verdad; si no, undefined (2026-10-16).
 *
 * Es la regla de la rejilla del portal: la nota vale mientras el hueco está
 * vacío, y en cuanto llega el producto —venga de donde venga— manda él. El
 * "vacío" es el de `displayProductNames`, que ya descarta el producto en
 * blanco con el que nacen esas plannings y el placeholder de self-managed, así
 * que vale igual para los dos flujos y para todos los países.
 *
 * Se enseña con `DraftProduct`: en cursiva y sin etiqueta, como si fuera el
 * producto (Daniel, 2026-10-16).
 */
export function productDraftOf(p: Position): string | undefined {
  if (displayProductNames(p)) return undefined;
  const draft = (p.productDraft ?? '').toString().trim();
  return draft || undefined;
}

/**
 * Lo que va en el badge verde de proveedor de la card. Clásica: el texto
 * libre o, si no, la fábrica. Self-managed: el texto libre o, si no, la
 * fábrica SOLO cuando tiene nombre: la de relleno de la maestra (con la que
 * nace toda planning self-managed) no lo tiene, y una con nombre es una
 * fábrica real elegida en el portal (2026-09-10).
 */
export function supplierBadgeOf(p: Position): string | undefined {
  const supplier = (p.supplierName ?? '').toString().trim();
  if (supplier) return supplier;
  const factory = (p.factoryName ?? '').toString().trim();
  return factory || undefined;
}
